import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  PhoneIcon,
  XMarkIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import AnimatedModal from '../ui/AnimatedModal';
import { useAuth } from '../../contexts/AuthContext';
import QualifyingAgentSetupStep, { DEFAULT_CALL_HOURS, DEFAULT_TIME_ZONE, callHoursError } from './QualifyingAgentSetupStep';
import QualifyingAgentQuestionsEditor, {
  INTRO_TEXT,
  defaultBrief,
  currentIntroText,
  toStoredText,
  isKnockout,
  answerTypeOf,
  expectedAnswerOf,
  knockoutProblemOf,
} from './QualifyingAgentQuestionsEditor';
import QualifyingAgentFaqStep, { DEFAULT_FALLBACK_REPLY, cleanFaqs } from './QualifyingAgentFaqStep';
import QualifyingAgentPreviewPanel from './QualifyingAgentPreviewPanel';

/**
 * Create or edit a Qualifying Agent screening.
 *
 * ── What changed and why ──────────────────────────────────────────────────────────────────
 *
 * This was three hand-rolled steps in a shell that jumped from max-w-2xl to max-w-5xl between
 * them, with a stepper of unlabelled 20px dots, and with steps 1 and 2 rendering their own
 * footers *inside the scrolling body* while step 3 had a real one. So the Cancel and Next
 * buttons moved — and sometimes scrolled out of view — on every step. That is the single
 * biggest reason the flow read as confusing, and it is fixed by giving the modal one fixed
 * size and one sticky footer for every step.
 *
 * It now matches the create-screening and assessment flows it sits beside: `AnimatedModal`
 * (which the automations modal already uses, and which locks body scroll), a **named** stepper
 * with the same emerald-when-complete treatment, a fixed `max-w-5xl h-[min(82dvh,820px)]`
 * shell, and one footer pinned to the bottom.
 *
 * Three steps became two — see QualifyingAgentSetupStep for why the channel step was folded in.
 *
 * Props:
 *  isOpen, onClose, jobId, job, onSaved
 *  screening — when present, the modal edits that screening instead of creating one.
 */

// A new screening opens with the intro and the role brief, both knockouts; the recruiter adds
// the rest (QualifyingAgentQuestionsEditor). The brief is kept in the list either way; whether the
// call plays it is the screening's role-brief switch (`qualifyingAgentRoleBrief`).
const openingQuestions = (jobDescription) => [
  { id: 'intro', text: INTRO_TEXT, kind: 'intro', knockout: true },
  { id: 'willingness', text: defaultBrief(jobDescription), kind: 'willingness', knockout: true },
];

// Which channel goes first in "both" is an org setting (super admin → Qualifying Agent), not the recruiter's.
const DEFAULT_CHANNEL = { mode: 'both' };
const DEFAULT_DETAILS = {
  screeningName: '', orgName: '', role: '', location: '', locationType: 'onsite',
  salaryMin: '', salaryMax: '', currency: 'INR', showSalary: false, showRole: true, showLocation: true,
  showLocType: false,
  language: 'en', voiceGender: 'female',
  callWindowStart: DEFAULT_CALL_HOURS.start, callWindowEnd: DEFAULT_CALL_HOURS.end,
  callTimeZone: DEFAULT_TIME_ZONE,
};

const ALL_STEPS = [
  { id: 'setup', label: 'Role & channel' },
  { id: 'questions', label: 'Questions' },
  { id: 'faq', label: 'Candidate questions' },
];

// The Additional context box (orgs without the Candidate questions step). Mirrors
// QUALIFYING_AGENT_CONTEXT_MAX_CHARS on the server, which also caps it.
const CONTEXT_MAX_CHARS = 2000;

const SUBTITLES = {
  setup: 'Who is calling, about what, and on which channel.',
  questions: 'What the agent says and asks, in order.',
  faq: 'How the agent answers when the candidate asks something.',
};

export default function QualifyingAgentModal({ isOpen, onClose, jobId, job, onSaved, screening = null }) {
  const { user } = useAuth();
  const isEdit = !!screening;
  // Super admin, per org (Client Configurations → Qualifying Agent): clients whose candidates ask a
  // lot get the full Candidate questions step; everyone else one free-text box on the Questions step.
  const candidateQuestionsStep = user?.features?.qualifying_agent_candidate_questions === true;
  const STEPS = candidateQuestionsStep ? ALL_STEPS : ALL_STEPS.slice(0, 2);

  const [step, setStep] = useState('setup');
  const [channel, setChannel] = useState(DEFAULT_CHANNEL);
  const [details, setDetails] = useState(DEFAULT_DETAILS);
  const [questions, setQuestions] = useState(() => openingQuestions(''));
  const [faqs, setFaqs] = useState([]);
  const [fallbackReply, setFallbackReply] = useState(DEFAULT_FALLBACK_REPLY);
  const [additionalContext, setAdditionalContext] = useState('');
  // Whether the call plays the role brief after the intro. Off for a new screening (22 Oct,
  // product); a screening saved before the switch existed has it on (unset = on, server too).
  const [roleBriefEnabled, setRoleBriefEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  // The role brief the server last wrote (or the prefill), and the step-1 facts it was written
  // from. While the brief still reads exactly like it, the recruiter has not touched it and it is
  // rewritten when step 1 changes; once edited by hand it is left alone and flagged as stale.
  const [autoBrief, setAutoBrief] = useState(null);
  const [briefWriting, setBriefWriting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setStep('setup');

    // ── Edit: load from the screening, never from the job post ─────────────────────────────
    if (isEdit) {
      setChannel({ mode: screening.qualifyingAgentMode || 'both' });
      setDetails({
        screeningName: screening.name || '',
        orgName: screening.qualifyingAgentOrgName || '',
        role: screening.qualifyingAgentRole || '',
        location: screening.qualifyingAgentLocation || '',
        locationType: screening.qualifyingAgentLocType || 'onsite',
        salaryMin: screening.qualifyingAgentSalaryMin || '',
        salaryMax: screening.qualifyingAgentSalaryMax || '',
        currency: screening.qualifyingAgentCurrency || 'INR',
        showSalary: !!(screening.qualifyingAgentSalaryMin || screening.qualifyingAgentSalaryMax),
        showRole: screening.qualifyingAgentShowRole !== false,
        showLocation: screening.qualifyingAgentShowLocation !== false,
        // Saved before the work type had its own switch: it followed the location's.
        showLocType: (screening.qualifyingAgentShowLocType ?? screening.qualifyingAgentShowLocation) !== false,
        language: screening.qualifyingAgentLanguage || 'en',
        voiceGender: screening.qualifyingAgentVoiceGender || 'female',
        callWindowStart: screening.qualifyingAgentCallWindowStart || DEFAULT_CALL_HOURS.start,
        callWindowEnd: screening.qualifyingAgentCallWindowEnd || DEFAULT_CALL_HOURS.end,
        callTimeZone: screening.qualifyingAgentCallTimeZone || DEFAULT_TIME_ZONE,
      });
      setQuestions(
        Array.isArray(screening.qualifyingAgentQuestions) && screening.qualifyingAgentQuestions.length
          ? screening.qualifyingAgentQuestions.map((q) =>
              q.kind === 'intro' ? { ...q, text: currentIntroText(q.text) } : { ...q }
            )
          : openingQuestions(screening.description)
      );
      // The saved brief is the recruiter's: never rewritten without them asking.
      setAutoBrief(null);
      setFaqs((screening.qualifyingAgentFaqs || []).map((f, i) => ({ id: `faq_saved_${i}`, ...f })));
      setFallbackReply(screening.qualifyingAgentFallbackReply || DEFAULT_FALLBACK_REPLY);
      setAdditionalContext(screening.qualifyingAgentContext || '');
      setRoleBriefEnabled(screening.qualifyingAgentRoleBrief !== false);
      return;
    }

    // ── Create: prefill from the job post ──────────────────────────────────────────────────
    setChannel(DEFAULT_CHANNEL);
    const prefilled = openingQuestions(job?.description);
    setQuestions(prefilled);
    setAutoBrief({ text: prefilled[1].text, key: null });
    setFaqs([]);
    setFallbackReply(DEFAULT_FALLBACK_REPLY);
    setAdditionalContext('');
    setRoleBriefEnabled(false);

    setDetails({
      screeningName: job?.title ? `Qualifying — ${job.title}` : '',
      // Left blank rather than seeded from `user.company`. That is a free-text field each user
      // fills at signup, and it is routinely a person's own name — which then got read out loud
      // to candidates as the company calling them. The org lookup below is the authority.
      orgName: '',
      role: job?.title || '',
      location: job?.location || '',
      // In-office unless the recruiter picks otherwise (8 Oct): the job post's own value is
      // often the AI job generator's default ("remote"), not a decision anyone made.
      locationType: 'onsite',
      salaryMin: job?.salaryMin ?? '',
      salaryMax: job?.salaryMax ?? '',
      currency: job?.salaryCurrency || 'INR',
      // Role and city are told by default; salary and work type only when the recruiter turns
      // them on (16 Oct) — they made the role brief long and are often not decided yet.
      showSalary: false,
      showRole: true,
      showLocation: true,
      showLocType: false,
      language: 'en',
      voiceGender: 'female',
      callWindowStart: DEFAULT_CALL_HOURS.start,
      callWindowEnd: DEFAULT_CALL_HOURS.end,
      callTimeZone: DEFAULT_TIME_ZONE,
    });

    axios
      .get('/api/screenings/my-org')
      .then(({ data }) => {
        if (!data?.name) {
          return;
        }
        // Only fill a field that is still empty. This request is in flight while the modal is
        // already interactive, so an unconditional set overwrites whatever the recruiter typed
        // in the meantime — which is how a corrected company name silently reverted.
        setDetails((prev) => (prev.orgName.trim() ? prev : { ...prev, orgName: data.name }));
      })
      .catch(() => {
        /* non-fatal — the field is required and editable, so the recruiter fills it */
      });
  }, [isOpen, job, user, isEdit, screening]);

  const resetAndClose = () => {
    setStep('setup');
    onClose();
  };

  const stepIndex = STEPS.findIndex((s) => s.id === step);

  // Only the fields the agent cannot work without. Everything else has a sane empty behaviour,
  // documented on the field itself.
  const setupComplete = !!(details.screeningName.trim() && details.orgName.trim() && details.role.trim());
  // Call hours only matter when the agent calls; WhatsApp-only screenings never check them.
  const callHoursProblem =
    channel.mode === 'whatsapp' ? '' : callHoursError(details.callWindowStart, details.callWindowEnd, details.callTimeZone);
  // Every question carries an explicit knockout flag, so the server never has to guess by kind.
  const cleanedQuestions = questions
    .filter((q) => q.text.trim())
    // Intro and brief are fixed yes/no gates; the recruiter's questions carry their type and the
    // answer that counts as good (the score; on a knockout, the requirement the call checks).
    .map((q) =>
      q.kind === 'intro' || q.kind === 'willingness'
        ? { ...q, knockout: isKnockout(q) }
        : {
            ...q,
            knockout: isKnockout(q),
            answerType: answerTypeOf(q),
            expectedAnswer: expectedAnswerOf(q),
          }
    );
  // The recruiter's own questions (not the intro or the brief): with the brief off they are all
  // the call asks after "two minutes to talk?", so at least one is needed.
  const ownQuestionCount = cleanedQuestions.filter((q) => q.kind !== 'intro' && q.kind !== 'willingness').length;
  // A knockout needs the requirement it checks: its expected answer.
  const knockoutGap = cleanedQuestions.map((q, i) => ({ i, problem: knockoutProblemOf(q) })).find((x) => x.problem);
  const questionsProblem =
    cleanedQuestions.length === 0
      ? 'Add at least one question'
      : !roleBriefEnabled && ownQuestionCount === 0
        ? 'Add at least one question of your own, or turn the role brief on'
        : knockoutGap
          ? `Knockout question ${knockoutGap.i + 1}: ${knockoutGap.problem}`
          : '';
  // What the call will actually ask, for the preview.
  const askedQuestions = roleBriefEnabled ? questions : questions.filter((q) => q.kind !== 'willingness');
  const briefText = (questions.find((q) => q.kind === 'willingness')?.text || '')
    .replace(/\{\{role\}\}/gi, details.role.trim())
    .replace(/\{\{company\}\}/gi, details.orgName.trim())
    .trim();

  // Only what the recruiter chose to tell candidates goes into the brief.
  const briefInputs = {
    // On edit, screening.description is the saved brief itself, so the job post comes first.
    jobDescription: job?.description || screening?.description || '',
    company: details.orgName.trim(),
    role: details.showRole !== false ? details.role.trim() : '',
    // Sent only so the server can refuse a brief that names it anyway (it is in the job post).
    hiddenRole: details.showRole === false ? details.role.trim() : '',
    location: details.showLocation !== false ? details.location.trim() : '',
    locationType: details.showLocType !== false ? details.locationType || 'onsite' : '',
    salaryMin: details.showSalary ? String(details.salaryMin ?? '') : '',
    salaryMax: details.showSalary ? String(details.salaryMax ?? '') : '',
    currency: details.showSalary ? details.currency || 'INR' : '',
  };
  const briefKey = JSON.stringify(briefInputs);
  const currentBrief = questions.find((q) => q.kind === 'willingness')?.text || '';
  const briefStale = Boolean(autoBrief?.key) && autoBrief.key !== briefKey;

  const writeBrief = async () => {
    const key = briefKey;
    setBriefWriting(true);
    try {
      const { data } = await axios.post('/api/qualifying-sessions/role-brief', briefInputs);
      const text = toStoredText(data.brief, { orgName: details.orgName, role: details.role });
      setQuestions((prev) => prev.map((q) => (q.kind === 'willingness' ? { ...q, text } : q)));
      setAutoBrief({ text, key });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not write the role brief — edit it yourself or try again');
    } finally {
      setBriefWriting(false);
    }
  };

  const goToStep = (id) => {
    setStep(id);
    // New screening, brief on and untouched, and step 1 changed since it was written: write it now.
    if (id === 'questions' && roleBriefEnabled) {
      refreshBrief();
    }
  };

  const refreshBrief = () => {
    if (!briefWriting && autoBrief && currentBrief === autoBrief.text && autoBrief.key !== briefKey) {
      writeBrief();
    }
  };

  // Turning the brief on writes it from step 1 if it was never written (it is not written while
  // off: nobody would hear it).
  // A screening without a brief question (saved before it existed, or deleted from View
  // details) gets one from the job post, right after the intro.
  const toggleRoleBrief = (on) => {
    setRoleBriefEnabled(on);
    if (!on) {
      return;
    }
    if (!questions.some((q) => q.kind === 'willingness')) {
      const brief = openingQuestions(job?.description || screening?.description || '')[1];
      setQuestions((prev) => {
        const at = prev.findIndex((q) => q.kind === 'intro') + 1;
        return [...prev.slice(0, at), brief, ...prev.slice(at)];
      });
      return;
    }
    refreshBrief();
  };

  const handleSave = async () => {
    if (!setupComplete) {
      setStep('setup');
      toast.error('Screening name, company and role are required');
      return;
    }
    if (callHoursProblem) {
      setStep('setup');
      toast.error(`Call hours: ${callHoursProblem}`);
      return;
    }
    if (questionsProblem) {
      setStep('questions');
      toast.error(questionsProblem);
      return;
    }

    setSaving(true);
    const payload = {
      name: details.screeningName.trim(),
      // The role brief doubles as the job description the agent may answer from.
      description: briefText,
      qualifyingAgentQuestions: cleanedQuestions,
      qualifyingAgentMode: channel.mode,
      qualifyingAgentOrgName: details.orgName.trim() || undefined,
      qualifyingAgentRole: details.role.trim() || undefined,
      qualifyingAgentLocation: details.location.trim() || undefined,
      qualifyingAgentLocType: details.locationType || undefined,
      // Experience is no longer asked for or spoken; cleared so older screenings stop saying it.
      qualifyingAgentExp: '',
      qualifyingAgentShowRole: details.showRole !== false,
      qualifyingAgentShowLocation: details.showLocation !== false,
      qualifyingAgentShowLocType: details.showLocType !== false,
      qualifyingAgentRoleBrief: roleBriefEnabled,
      // Saved FAQs are always sent back unchanged when the step is hidden: screenings that have
      // them keep answering from them.
      qualifyingAgentFaqs: cleanFaqs(faqs),
      ...(candidateQuestionsStep
        ? { qualifyingAgentFallbackReply: fallbackReply.trim() || DEFAULT_FALLBACK_REPLY }
        : {
            qualifyingAgentContext: additionalContext.trim(),
            // No fallback box here: a new screening uses the agent's built-in "your HR team can
            // help" line; an edit leaves whatever the screening already had.
            ...(isEdit ? {} : { qualifyingAgentFallbackReply: '' }),
          }),
      qualifyingAgentSalaryMin: details.showSalary ? details.salaryMin || undefined : '',
      qualifyingAgentSalaryMax: details.showSalary ? details.salaryMax || undefined : '',
      qualifyingAgentCurrency: details.showSalary ? details.currency || undefined : '',
      // Always sent, even for WhatsApp-only screenings (where the call never happens): switching
      // the channel back to a call later must not silently reset the language the recruiter chose.
      qualifyingAgentLanguage: details.language || 'en',
      qualifyingAgentVoiceGender: details.voiceGender || 'female',
      // Always sent, like the language: switching to WhatsApp-only and back keeps them.
      qualifyingAgentCallWindowStart: details.callWindowStart || DEFAULT_CALL_HOURS.start,
      qualifyingAgentCallWindowEnd: details.callWindowEnd || DEFAULT_CALL_HOURS.end,
      qualifyingAgentCallTimeZone: details.callTimeZone || DEFAULT_TIME_ZONE,
    };

    try {
      if (isEdit) {
        await axios.put(`/api/screenings/${screening._id}`, payload);
        toast.success('Qualifying Agent updated');
      } else {
        await axios.post(`/api/screenings/job/${jobId}`, {
          ...payload,
          hasResumeScoring: false,
          hasAssessment: false,
          hasInterview: false,
          includeDocumentCollection: false,
          includeQualifyingAgent: true,
        });
        toast.success('Qualifying Agent screening created');
      }
      resetAndClose();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} screening`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={resetAndClose}
      closeOnBackdrop={false}
      zIndex={60}
      overflowHidden
      ariaLabelledBy="qualifying-agent-modal-title"
      contentClassName="max-w-5xl rounded-2xl"
    >
      <div className="flex h-[min(86dvh,860px)] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 px-6 py-4">
          <div className="rounded-xl bg-[#007BFF]/10 p-2 text-[#007BFF]">
            <PhoneIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="qualifying-agent-modal-title" className="text-lg font-bold text-gray-900">
              {isEdit ? 'Edit Qualifying Agent' : 'Qualifying Agent'}
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {SUBTITLES[step]}
            </p>
          </div>

          {/* Named stepper — same treatment as the create-screening flow next door. */}
          <nav className="hidden items-center gap-1 sm:flex" aria-label="Progress">
            {STEPS.map((s, i) => {
              const isActive = i === stepIndex;
              const isDone = i < stepIndex;
              return (
                <React.Fragment key={s.id}>
                  {i > 0 && <span className="mx-1 text-sm text-gray-300">&rarr;</span>}
                  <button
                    type="button"
                    onClick={() => (i === 0 || setupComplete) && goToStep(s.id)}
                    disabled={i > 0 && !setupComplete}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-[#007BFF]/10 text-[#007BFF]'
                        : 'text-gray-500 hover:bg-gray-50 disabled:hover:bg-transparent'
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-all ${
                        isActive
                          ? 'bg-[#007BFF] text-white'
                          : isDone
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isDone ? <CheckIcon className="h-3 w-3" /> : i + 1}
                    </span>
                    {s.label}
                  </button>
                </React.Fragment>
              );
            })}
          </nav>

          <button
            onClick={resetAndClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="min-w-0 flex-1 overflow-y-auto px-6 py-5">
            {step === 'setup' ? (
              <QualifyingAgentSetupStep
                value={details}
                channel={channel}
                onChange={setDetails}
                onChannelChange={setChannel}
              />
            ) : step === 'questions' ? (
              <>
                <QualifyingAgentQuestionsEditor
                  questions={questions}
                  onChange={setQuestions}
                  details={details}
                  briefTools={{ writing: briefWriting, stale: briefStale, onRewrite: writeBrief }}
                  roleBriefEnabled={roleBriefEnabled}
                  onRoleBriefToggle={toggleRoleBrief}
                  channelMode={channel.mode}
                />
                {!candidateQuestionsStep && (
                  <AdditionalContextBox value={additionalContext} onChange={setAdditionalContext} />
                )}
              </>
            ) : (
              <QualifyingAgentFaqStep
                faqs={faqs}
                onChange={setFaqs}
                fallback={fallbackReply}
                onFallbackChange={setFallbackReply}
              />
            )}
          </div>

          {/* The preview is what makes the wording decisions reviewable, so it stays up on both
              steps instead of appearing only at the end. Hidden below lg — at that width it
              would take the space the editor needs. */}
          <aside className="hidden w-[340px] shrink-0 flex-col overflow-hidden border-l border-gray-100 bg-gray-50/50 px-4 pb-2 pt-4 lg:flex">
            <QualifyingAgentPreviewPanel channel={channel} details={details} questions={askedQuestions} />
          </aside>
        </div>

        {/* ── Footer — one, sticky, identical on every step ─────────────────── */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-gray-50/50 px-6 py-4">
          <button
            type="button"
            onClick={() => (stepIndex === 0 ? resetAndClose() : setStep(STEPS[stepIndex - 1].id))}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50"
          >
            {stepIndex === 0 ? 'Cancel' : (<><ArrowLeftIcon className="h-4 w-4" />Back</>)}
          </button>

          <div className="flex items-center gap-3">
            {stepIndex < STEPS.length - 1 ? (
              <button
                type="button"
                data-testid={stepIndex === 0 ? 'qa-setup-next-btn' : 'qa-questions-next-btn'}
                onClick={() => goToStep(STEPS[stepIndex + 1].id)}
                disabled={!setupComplete || (stepIndex === 1 && Boolean(questionsProblem))}
                className="inline-flex items-center gap-2 rounded-xl bg-[#007BFF] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#0062CC] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Next
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || briefWriting || !setupComplete || Boolean(questionsProblem)}
                data-testid="qualifying-agent-save-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-[#007BFF] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#0062CC] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving && (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-transparent" />
                )}
                {saving
                  ? (isEdit ? 'Saving…' : 'Creating…')
                  : (isEdit ? 'Save changes' : 'Create screening')}
              </button>
            )}
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
}

/**
 * Orgs without the Candidate questions step: one optional box the agent answers from when a
 * candidate asks about the job. Facts only — the agent quotes it, never takes orders from it
 * (server: turnJudge.js keeps it as data), and says the built-in "your HR team can help" line
 * for anything it does not cover.
 */
function AdditionalContextBox({ value, onChange }) {
  return (
    <section className="mt-6 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
      <div className="flex items-center gap-2">
        <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-900">Additional context</h4>
        <span className="text-xs text-gray-400">Optional</span>
      </div>
      <p className="mt-0.5 text-xs text-gray-500">
        Anything a candidate may ask about — working days, shift timings, salary in hand, location,
        training. If a candidate asks, the agent answers only from this; for anything else it says
        the HR team will help.
      </p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, CONTEXT_MAX_CHARS))}
        rows={4}
        maxLength={CONTEXT_MAX_CHARS}
        data-testid="qa-additional-context"
        placeholder="e.g. 6 days a week, Sunday off. Day shift 10 AM – 7 PM. In-hand salary ₹15,000 per month. Training for the first week."
        className="mt-2.5 w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20"
      />
      <p className="mt-1 text-right text-[11px] tabular-nums text-gray-400">
        {value.length}/{CONTEXT_MAX_CHARS}
      </p>
    </section>
  );
}
